import { useState } from "react";
import { Download, Loader2, Check, Share2, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoginArea } from "@/components/auth/LoginArea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useUploadFile } from "@/hooks/useUploadFile";
import { useToast } from "@/hooks/useToast";
import { track } from "@/lib/track";
import {
  certificateToBlob,
  certificateFileName,
  type CertificateData,
} from "@/lib/certificateImage";

interface ShareCertificateProps {
  data: CertificateData;
  /** Text used for the social post. */
  shareText: string;
  hashtags?: string[];
}

// X (Twitter) logo — lucide dropped the twitter icon.
function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function ShareCertificate({
  data,
  shareText,
  hashtags = ["BitcoinCash", "BCH"],
}: ShareCertificateProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending: publishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: uploading } = useUploadFile();
  const { toast } = useToast();

  const [downloaded, setDownloaded] = useState(false);
  const [postedNostr, setPostedNostr] = useState(false);
  const [nativeSharing, setNativeSharing] = useState(false);

  const canNativeShareFiles =
    typeof navigator !== "undefined" && "canShare" in navigator;

  async function makeFile(): Promise<File> {
    const blob = await certificateToBlob(data);
    return new File([blob], certificateFileName(data.name), {
      type: "image/png",
    });
  }

  async function downloadImage() {
    try {
      track("certificate_download");
      const blob = await certificateToBlob(data);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = certificateFileName(data.name);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2000);
    } catch {
      toast({
        title: "Couldn't create image",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  }

  async function nativeShare() {
    setNativeSharing(true);
    try {
      const file = await makeFile();
      const shareData: ShareData = {
        title: "BCH School Certificate",
        text: shareText,
        files: [file],
      };
      if (navigator.canShare?.(shareData)) {
        track("certificate_share");
        await navigator.share(shareData);
      } else {
        // Fallback: share text/url only.
        await navigator.share({
          title: "BCH School Certificate",
          text: shareText,
          url: "https://bchschool.com",
        });
      }
    } catch {
      // User cancelled or unsupported — ignore silently.
    } finally {
      setNativeSharing(false);
    }
  }

  function shareOnX() {
    track("certificate_share");
    // X can't attach a local image via the intent URL, so we download it first
    // and prompt the user to attach it to their post.
    void downloadImage();
    const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText,
    )}&hashtags=${encodeURIComponent(hashtags.join(","))}`;
    window.open(xUrl, "_blank", "noopener,noreferrer");
    toast({
      title: "Certificate image saved 📥",
      description: "Attach the downloaded image to your post on X.",
    });
  }

  async function shareOnNostr() {
    try {
      const file = await makeFile();
      // Returns NIP-94-compatible tags; tags[0] is ["url", <fileUrl>].
      const tags = await uploadFile(file);
      const imageUrl = tags[0]?.[1];
      // Flatten all NIP-94 tags into a single imeta tag value.
      const imeta = tags.map(([name, value]) => `${name} ${value}`);

      const content = imageUrl
        ? `${shareText}\n\n${imageUrl}\n\n${hashtags.map((h) => `#${h}`).join(" ")}`
        : `${shareText}\n\n${hashtags.map((h) => `#${h}`).join(" ")}`;

      await publish({
        kind: 1,
        content,
        tags: [
          ...hashtags.map((h) => ["t", h.toLowerCase()]),
          ...(imageUrl ? [["imeta", ...imeta]] : []),
        ],
      });

      setPostedNostr(true);
      track("certificate_share");
      toast({
        title: "Shared to Nostr! 🎉",
        description: "Your certificate has been posted with the image.",
      });
    } catch {
      toast({
        title: "Couldn't post to Nostr",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    }
  }

  const busyNostr = uploading || publishing;

  return (
    <div className="space-y-3">
      {/* Primary: get the image */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button className="flex-1" onClick={downloadImage}>
          {downloaded ? (
            <Check className="mr-2 h-4 w-4" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          {downloaded ? "Image saved!" : "Download image"}
        </Button>

        {canNativeShareFiles && (
          <Button
            variant="outline"
            className="flex-1"
            onClick={nativeShare}
            disabled={nativeSharing}
          >
            {nativeSharing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ImageIcon className="mr-2 h-4 w-4" />
            )}
            Share…
          </Button>
        )}
      </div>

      {/* Social posts */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button variant="outline" className="flex-1" onClick={shareOnX}>
          <XLogo className="mr-2 h-4 w-4" /> Share on X
        </Button>

        {user ? (
          <Button
            className="flex-1"
            onClick={shareOnNostr}
            disabled={busyNostr || postedNostr}
          >
            {busyNostr ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : postedNostr ? (
              <Check className="mr-2 h-4 w-4" />
            ) : (
              <Share2 className="mr-2 h-4 w-4" />
            )}
            {busyNostr
              ? "Posting…"
              : postedNostr
                ? "Shared to Nostr"
                : "Share on Nostr"}
          </Button>
        ) : (
          <div className="flex-1">
            <LoginArea className="w-full" />
          </div>
        )}
      </div>

      {!user && (
        <p className="text-center text-xs text-muted-foreground">
          Log in with Nostr to post your certificate image to the network.
        </p>
      )}
    </div>
  );
}
